/**
 * ServerConfiguredPanel
 *
 * Read-only setup panel for server-configured connectors.
 * Rendered for connectors like `arcaInvoicing` that declare neither the
 * `oauth` nor `api-key` capability — the connection is provisioned and
 * authenticated on the server side, so the user has no credentials to
 * enter and no OAuth flow to run.
 *
 * Communicates that the connector is server-managed and exposes no setup
 * actions. Purely presentational.
 */

import { Badge, Group, Stack, Text } from '@mantine/core';
import { IconServerBolt } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { integrationsTestIds } from '@domains/integrations/testids';
import styles from './SetupPanel.module.css';

export type ServerConfiguredPanelProps = {
  /** Connector id, used for the data-testid. */
  connectorId: string;
  /** Human-readable connector label. */
  connectorLabel: string;
  /**
   * Whether the connector is currently active on the server.
   * Defaults to `true` — server-configured connectors are assumed wired
   * unless the caller explicitly says otherwise.
   */
  isActive?: boolean;
};

const ServerConfiguredPanel = ({
  connectorId,
  connectorLabel,
  isActive = true
}: ServerConfiguredPanelProps ) => {
  const { t } = useTranslation( 'integrations' );

  return (
    <Stack
      gap="sm"
      data-testid={integrationsTestIds.connectorServerConfigured( connectorId )}
    >
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <IconServerBolt size={16} className={styles.headerIconMuted} />
          <Text className={styles.title}>
            {t(
              'setup_panel.serverConfigured.title',
              'Conector administrado por el servidor'
            )}
          </Text>
        </Group>
        <Badge
          size="sm"
          radius="xs"
          variant="light"
          color={isActive ? 'green' : 'gray'}
        >
          {isActive
            ? t( 'setup_panel.serverConfigured.status_active', 'Activo' )
            : t( 'setup_panel.serverConfigured.status_inactive', 'Inactivo' )}
        </Badge>
      </Group>

      <Text className={styles.helpText}>
        {t(
          'setup_panel.serverConfigured.help',
          '{{connector}} se configura del lado del servidor. No es necesario ingresar credenciales aquí.',
          { connector: connectorLabel }
        )}
      </Text>

      <div className={styles.statusRow}>
        <Text className={styles.statusLabel}>
          {t(
            'setup_panel.serverConfigured.status_label',
            'Estado de la conexión'
          )}
        </Text>
        <Text className={styles.helpText}>
          {isActive
            ? t(
                'setup_panel.serverConfigured.status_active_detail',
                'La integración está habilitada y lista para usarse.'
              )
            : t(
                'setup_panel.serverConfigured.status_inactive_detail',
                'La integración no está habilitada. Contacta a soporte.'
              )}
        </Text>
      </div>
    </Stack>
  );
};

export default ServerConfiguredPanel;
